/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { GridColDef, GridColumnVisibilityModel, GridToolbarContainer, GridToolbarExportContainer, GridToolbarProps, GridPrintExportMenuItem, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarFilterButton, GridValidRowModel } from '@mui/x-data-grid-pro';
import { Box, Dialog, DialogContent, DialogTitle, LinearProgress, MenuItem, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { utils, writeFileXLSX } from 'xlsx';
import { useNotification } from 'hooks/useNotification';

export type ExportProgress = {
  phase: 'fetching' | 'generating';
  percentage: number;
};

export type ProgressCallback = (progress: ExportProgress) => void;

export type ExcelExportParams<Row extends GridValidRowModel> = {
  columns?: GridColDef<Row>[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  fetchAll: (onProgress: ProgressCallback) => Promise<Row[]>;
  fileNamePrefix: string;
};

export const useExcelExport = <Row extends GridValidRowModel>(params: ExcelExportParams<Row>) => {
  const { formatMessage } = useIntl();
  const { notifyFailure } = useNotification();
  const { columns, columnVisibilityModel, fetchAll, fileNamePrefix } = params;
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress>({ phase: 'fetching', percentage: 0 });

  const visibleColumns = useMemo(() => {
    if (!columns) return [];

    return columns.filter(column =>
      !column.disableExport &&
      (columnVisibilityModel?.[column.field] ?? true)
    );
  }, [columnVisibilityModel, columns]);

  const formatColumnValue = useCallback((column: GridColDef<Row>, row: Row) => {
    const rawValue = column.valueGetter
      ? column.valueGetter({ row, field: column.field } as unknown as Parameters<NonNullable<GridColDef['valueGetter']>>[0])
      : (row as Record<string, unknown>)[column.field];

    const formattedValue = column.valueFormatter
      ? column.valueFormatter({ value: rawValue, field: column.field, row, colDef: column } as unknown as Parameters<NonNullable<GridColDef['valueFormatter']>>[0])
      : rawValue;

    if (formattedValue instanceof Date) {
      return dayjs(formattedValue).format('YYYY-MM-DD');
    }

    if (Array.isArray(formattedValue)) {
      return formattedValue.join(', ');
    }

    return formattedValue ?? '';
  }, []);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    setProgress({ phase: 'fetching', percentage: 0 });

    try {
      const allRows = await fetchAll(setProgress);

      setProgress({ phase: 'generating', percentage: 0 });

      const rowsToExport = allRows.map((row, index) => {
        const record: Record<string, unknown> = {};

        visibleColumns.forEach(column => {
          const header = column.headerName ?? column.field;
          record[header] = formatColumnValue(column, row);
        });

        if (index % 100 === 0) {
          setProgress({ phase: 'generating', percentage: Math.round((index / allRows.length) * 100) });
        }

        return record;
      });

      setProgress({ phase: 'generating', percentage: 100 });

      const worksheet = utils.json_to_sheet(rowsToExport);
      const workbook = utils.book_new();

      utils.book_append_sheet(workbook, worksheet, 'Data');
      writeFileXLSX(workbook, `${fileNamePrefix}-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    } catch (error) {
      notifyFailure(formatMessage({ id: 'Failed to export the data to Excel: {error}' }, { error: error instanceof Error ? error.message : 'Unknown error' }));
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, fetchAll, fileNamePrefix, visibleColumns, formatColumnValue, notifyFailure, formatMessage]);

  const exportDisabled = !visibleColumns.length;

  return { handleExport, exporting: isExporting, exportDisabled, visibleColumns, progress };
};

export type ExportToolbarProps = GridToolbarProps & {
  onExport: () => void;
  exporting: boolean;
  exportDisabled: boolean;
  exportLabel: string;
  progress?: ExportProgress;
};

export const ExcelExportMenuItem: React.FC<Omit<ExportToolbarProps, 'onExport' | 'exportLabel' | 'progress'> & { onClick: () => void; exportLabel: string; }> = ({ onClick, exporting, exportDisabled, exportLabel }) => (
  <MenuItem onClick={onClick} disabled={exportDisabled || exporting}>
    {exportLabel}
  </MenuItem>
);

export const ExportProgressDialog: React.FC<{ open: boolean; progress: ExportProgress }> = ({ open, progress }) => {
  const { formatMessage } = useIntl();

  const phaseLabel = progress.phase === 'fetching'
    ? formatMessage({ id: 'Fetching data...' })
    : formatMessage({ id: 'Generating Excel file...' });

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>{formatMessage({ id: 'Export in progress' })}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {phaseLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress variant="determinate" value={progress.percentage} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
            {progress.percentage}%
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export const ExportToolbar: React.FC<ExportToolbarProps> = ({ onExport, exporting, exportDisabled, exportLabel, progress }) => (
  <>
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExportContainer>
        <ExcelExportMenuItem
          onClick={onExport}
          exporting={exporting}
          exportDisabled={exportDisabled}
          exportLabel={exportLabel}
        />
        <GridPrintExportMenuItem />
      </GridToolbarExportContainer>
    </GridToolbarContainer>
    {progress && <ExportProgressDialog open={exporting} progress={progress} />}
  </>
);

